import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { isNil } from "lodash";
import { Subject } from "rxjs";

interface DataDogConfiguration {
  applicationId: string;
  clientToken: string;
  sampleRate: number;
  environment: string;
}

// Ensure stubs are added in case RUM cannot be loaded
interface IDatadogRum {
  addAction: Function;
  addRumGlobalContext: Function;
  startSessionReplayRecording: Function;
  stopSessionReplayRecording: Function;
  setUser: Function;
}

@Injectable()
export class ConfigurationService {
  public apiUrl: string;
  public patientChartUrl: string;
  public alertsUrl: string;
  public analyticsUrl: string;
  public assessmentUrl: string;
  public betaAppUrl: string;
  public apiCommonUrl: string;
  public documentManagementUrl: string;
  public logoutUrl: string;
  public medicationUrl: string;
  public planOfCareUrl: string;
  public showOrderManagement: boolean;
  public billingUrl: string;
  public featureToggleUrl: string;
  public referralUrl: string;
  public hisAdmissionUrl: string;
  public signatureUrl: string;
  public clinicalUrl: string;
  public reportsRbacUrl: string;
  public notesUrl: string;
  public activitiesUrl: string;
  public facilitiesUrl: string;
  public codesUrl: string;
  public resourceManagementUrl: string;
  public reportsUrl: string;
  public loggingUrl: string;
  public patientChartStatusUrl: string;
  public classificationUrl: string;
  public bereavementUrl: string;
  public marUrl: string;
  public reportExecutionUrl: string;
  public volunteerManagementUrl: string;
  public casUrl: string;
  public usersUrl: string;
  public accessUrl: string;
  private config: any;
  public configSubject$: Subject<any> = new Subject<any>();
  public visitTemplatesV2: boolean;
  public localVisitTemplate: string;
  public isLocal: boolean;
  public localPatientChartApi: string;
  public isPatientChartLocal: boolean;
  public localPatientBenefitsApi: string;
  public isPatientBenefitsLocal: boolean;
  public companyServiceUrl: string;
  public workgroupServiceUrl: string;
  public dataDog: DataDogConfiguration;
  public localEndpoints: any;
  public spsUrl: any;
  public sockets: string;
  public rum: IDatadogRum;
  public featureToggleOverrides: Record<string, boolean>;

  constructor(private _http: HttpClient) {}

  public loadConfig() {
    console.log("Loading ...");
    return this._http
      .get("./assets/config/config.json")
      .toPromise()
      .then((config: any) => {
        this.config = config;
        this.initDataDog();
        this.configSubject$.next(this.config);
      })
      .catch((err: any) => {
        console.error(err);
        return Promise.reject(null);
      });
  }

  getConfig() {
    return this.config;
  }

  /**
   * Resolves the IDG service URL using a deployed-config-first chain.
   *
   * Priority:
   *   1. config.idgUrl                                  (deployed-config seam — set per-env to flip IDG to EKS idg-v2)
   *   2. config.isLocal && config.localEndpoints.idgUrl (local-dev override)
   *   3. config.apiUrl + "/idg"                         (legacy fallback)
   *
   * Centralizes the duplicated conditional formerly copy-pasted across 15+
   * clinical-ui services so future config changes are a one-file edit.
   */
  resolveIdgUrl(): string {
    const config = this.getConfig();
    if (config.idgUrl) {
      return config.idgUrl;
    }
    if (
      config.isLocal &&
      config.localEndpoints &&
      config.localEndpoints.idgUrl
    ) {
      return config.localEndpoints.idgUrl;
    }
    return config.apiUrl + "/idg";
  }

  /**
   * Resolves the IDG Summary service URL.
   *
   * The IDG Summary endpoints (POST /meetings/{m}/summary,
   * GET /meetings/{m}/summary/status, GET/DELETE /meetings/{m}/{p}/summary)
   * live on EKS idg-v2, NOT on legacy idg-trinity. They need their own URL
   * so we can route only the summary calls to EKS while the rest of the
   * IDG surface stays on the legacy path until the full migration lands.
   *
   * Priority:
   *   1. config.idgSummaryUrl                                  (deployed-config seam — set per-env when the env's EKS edge is wired)
   *   2. config.isLocal && config.localEndpoints.idgSummaryUrl (local-dev override)
   *   3. resolveIdgUrl()                                       (fall back to the broader IDG URL — legacy /idg if unset)
   *
   * QA note (2026-05-23): not set in config.qa.json yet because the QA
   * AWS API Gateway /idg-v2/* path is missing CORS + auth wiring — see
   * DEV-88. Until that lands, callers fall through to resolveIdgUrl()
   * which returns the legacy /idg path (which 404s on summary endpoints,
   * matching the pre-CO-2218 behaviour rather than the worse CORS-error
   * state PR #1448 produced).
   */
  resolveIdgSummaryUrl(): string {
    const config = this.getConfig();
    if (config.idgSummaryUrl) {
      return config.idgSummaryUrl;
    }
    if (
      config.isLocal &&
      config.localEndpoints &&
      config.localEndpoints.idgSummaryUrl
    ) {
      return config.localEndpoints.idgSummaryUrl;
    }
    return this.resolveIdgUrl();
  }

  /**
   * Resolves the patient-charts service URL using a deployed-config-first chain.
   *
   * Priority:
   *   1. config.patientChartsUrl                                 (deployed-config seam — set per-env to flip patient-charts to EKS)
   *   2. config.isLocal && config.localEndpoints.patientCharts   (local-dev override — used by config.dev-eks.json to point at the public Envoy edge)
   *   3. config.apiUrl                                           (legacy fallback — bare apiUrl, no path suffix; callsites already append "/patient-charts/...")
   *
   * Centralizes the duplicated conditional formerly copy-pasted across 10
   * clinical-ui files so future config changes are a one-file edit. Mirrors
   * resolveIdgUrl() above (REPO-366).
   */
  resolvePatientChartsUrl(): string {
    const config = this.getConfig();
    if (config.patientChartsUrl) {
      return config.patientChartsUrl;
    }
    if (
      config.isLocal &&
      config.localEndpoints &&
      config.localEndpoints.patientCharts
    ) {
      return config.localEndpoints.patientCharts;
    }
    return config.apiUrl;
  }

  /**
   * Resolves the Eva chat backend URL.
   *
   * CO-3309 dark ship: no backend is deployed yet, so config.evaUrl is
   * committed as "" in every env. Unlike resolveIdgUrl()/resolvePatientChartsUrl(),
   * there is no legacy URL to fall back to - a falsy return means "Eva is not
   * wired in this environment" and callers MUST treat it as such (see
   * EvaService.chat()/createTicket()).
   *
   * Priority:
   *   1. config.evaUrl                                  (deployed-config seam - set per-env once the Eva backend is deployed)
   *   2. config.isLocal && config.localEndpoints.evaUrl  (local-dev override)
   *   3. "" (no fallback exists)
   */
  resolveEvaUrl(): string {
    const config = this.getConfig();
    if (config.evaUrl) {
      return config.evaUrl;
    }
    if (
      config.isLocal &&
      config.localEndpoints &&
      config.localEndpoints.evaUrl
    ) {
      return config.localEndpoints.evaUrl;
    }
    return "";
  }

  /**
   * Resolves the QAPIplus export service URL.
   *
   * CO-3842: no ALB route for qapiplus-service is confirmed in any env yet,
   * so config.qapiplusUrl is unset. Mirrors resolveEvaUrl() exactly - unlike
   * resolveIdgUrl()/resolvePatientChartsUrl(), there is no legacy URL to fall
   * back to, so a falsy return means "QAPIplus is not wired in this
   * environment" and callers MUST treat it as such (see
   * QapiPlusExportDataService.downloadAll() / the card's download()).
   *
   * Priority:
   *   1. config.qapiplusUrl                                  (deployed-config seam - set per-env once the qapiplus-service ALB route exists)
   *   2. config.isLocal && config.localEndpoints.qapiplusUrl  (local-dev override)
   *   3. "" (no fallback exists)
   */
  resolveQapiplusUrl(): string {
    const config = this.getConfig();
    if (config.qapiplusUrl) {
      return config.qapiplusUrl;
    }
    if (
      config.isLocal &&
      config.localEndpoints &&
      config.localEndpoints.qapiplusUrl
    ) {
      return config.localEndpoints.qapiplusUrl;
    }
    return "";
  }

  static loadJson<T>(onError = (event) => null): T {
    const request = new XMLHttpRequest();
    request.open("GET", "./assets/config/config.json", false);
    request.onerror = onError;
    if (request.overrideMimeType) {
      request.overrideMimeType("application/json");
    }
    request.send();
    if (request.status === 200) {
      return <T>JSON.parse(request.responseText);
    }
    return <T>JSON.parse("{ level: NgxLoggerLevel.INFO }"); //this is default level if file not found
  }

  initDataDog(): void {
    const rum = (window as any).DD_RUM;
    if (this.config.dataDog && !isNil(rum)) {
      rum.init({
        applicationId: this.config.dataDog.applicationId,
        clientToken: this.config.dataDog.clientToken,
        site: "datadoghq.com",
        sampleRate: this.config.dataDog.sampleRate,
        replaySampleRate: this.config.dataDog.sampleRate,
        env: this.config.dataDog.environment,
        trackUserInteractions: true,
      });
      rum.startSessionReplayRecording();
      this.rum = rum;
    } else {
      this.rum = {
        addAction: () => {},
        addRumGlobalContext: () => {},
        startSessionReplayRecording: () => {},
        stopSessionReplayRecording: () => {},
        setUser: () => {},
      };
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/configuration/configuration.service.ts